import React from 'react';
import { Smartphone, Users, Zap, Shield, TrendingUp, Clock, CheckCircle, Eye, WifiOff } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const ParkingLotManagementApp: React.FC = () => {
  useDynamicSEO({
    title: "Digital Parking Lot Management App | Real-Time Control",
    description: "A powerful Parking Lot Management App built to automate operations, monitor occupancy, remove manual errors & improve customer experience. Ideal for commercial.",
        keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
    ogTitle: "Digital Parking Lot Management App | Real-Time Control",
    ogDescription: "A powerful Parking Lot Management App built to automate operations, monitor occupancy, remove manual errors & improve customer experience. Ideal for commercial."
  });

  const handleWhatsAppDemo = () => {
    const phoneNumber = "919384376329";
    const message = "Hi! I'm interested in learning more about Park Hive's Parking Lot Management App. Can you please provide me with a demo?";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const capabilities = [
    {
      icon: <Zap className="h-6 w-6 text-blue-600" />,
      title: "Create and configure a parking lot instantly without technical setup",
      description: "Quick setup for any parking facility size"
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-green-600" />,
      title: "Add pricing rules for bikes, cars, autos, vans, buses, or any vehicle type",
      description: "Flexible pricing for all vehicle categories"
    },
    {
      icon: <Clock className="h-6 w-6 text-purple-600" />,
      title: "Set hourly, daily, weekend, and monthly pass rates effortlessly",
      description: "Complete pricing flexibility for different time periods"
    },
    {
      icon: <Shield className="h-6 w-6 text-orange-600" />,
      title: "Manage revenue securely with UPI and digital payments",
      description: "Secure, transparent financial control"
    },
    {
      icon: <Smartphone className="h-6 w-6 text-indigo-600" />,
      title: "Monitor entry & exit through instant mobile scanning",
      description: "Real-time vehicle tracking with mobile cameras"
    },
    {
      icon: <Users className="h-6 w-6 text-teal-600" />,
      title: "Add and control staff access with role-based permissions",
      description: "Secure team management with accountability"
    }
  ];

  const benefits = [
    "No more guessing how many vehicles are parked",
    "No more depending on staff for numbers",
    "No more disputes about charges",
    "No more missing money"
  ];

  const operations = [
    "Mall parking spaces",
    "Multiplexes and entertainment venues",
    "Corporate campus parking",
    "Hospital parking facilities",
    "Tourist attractions",
    "Residential communities",
    "Transport terminals",
    "Temporary event parking"
  ];

  const faqs = [
    {
      question: "Can staff without technical skills operate the app?",
      answer: "Yes — the interface is simple, and training takes only a few minutes."
    },
    {
      question: "Does it support temporary or seasonal operations?",
      answer: "Absolutely — events and temporary lots use the app seamlessly."
    },
    {
      question: "Do I need new hardware?",
      answer: "No — a phone is enough to start. Hardware integration is optional."
    },
    {
      question: "Can I monitor collections remotely?",
      answer: "Yes — everything updates in real time and can be viewed anytime."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="bg-blue-600 p-4 rounded-2xl">
                <Smartphone className="h-16 w-16 text-white" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Parking Lot <span className="text-blue-600">Management App</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-8">
              A Powerful Mobile App to Control, Monitor, and Automate Your Parking Lot with Confidence & Real-Time Visibility
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Running a parking lot shouldn't feel like firefighting. Modern parking operations demand precision — not paper. 
              Speed — not confusion. Accountability — not assumptions.
            </p>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Everything you need to run a professional parking system lives inside your smartphone.</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-blue-50 rounded-full p-4 w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-blue-600" />
                  </div>
                  <p className="font-semibold text-gray-900">No bulky hardware</p>
                </div>
                <div className="text-center">
                  <div className="bg-green-50 rounded-full p-4 w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                    <Zap className="h-8 w-8 text-green-600" />
                  </div>
                  <p className="font-semibold text-gray-900">No complicated installations</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-50 rounded-full p-4 w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                    <Users className="h-8 w-8 text-purple-600" />
                  </div>
                  <p className="font-semibold text-gray-900">No long staff training</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Book a Live Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="border border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-50 transition-colors duration-200"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Control Not Complexity Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Built for Owners Who Want Control, Not Complexity
            </h2>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed text-center mb-6">
                With the Park Hive app, owners can create and manage their parking space, add team members, 
                set pricing policies, scan vehicles for instant entry and exit, handle digital payments, 
                and monitor revenue — from anywhere, at any time.
              </p>
              <div className="text-center">
                <p className="text-xl font-bold text-blue-600">
                  The power to run parking professionally fits in your pocket.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">📱 Mobile-First Design</h4>
                <p className="text-gray-700">
                  Complete parking management from your smartphone. No need for expensive hardware or complex installations.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">⚡ Instant Setup</h4>
                <p className="text-gray-700">
                  Create your parking "Hive" in minutes. Add team members, set pricing, and start operations immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Why a Parking Lot Management App Matters Today
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-blue-900 mb-3">👥 Customer Expectations</h4>
                <ul className="space-y-2 text-blue-800">
                  <li>• Fast entry and clean exit</li>
                  <li>• Clear pricing transparency</li>
                  <li>• Easy payment options</li>
                  <li>• Smooth overall experience</li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-green-900 mb-3">👨‍💼 Owner Challenges</h4>
                <ul className="space-y-2 text-green-800">
                  <li>• Revenue transparency needs</li>
                  <li>• Staff accountability requirements</li>
                  <li>• Operational efficiency demands</li>
                  <li>• Manual system limitations</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
                A smart parking app eliminates the pain:
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center bg-red-50 rounded-lg p-4">
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-white text-sm font-bold">✗</span>
                    </div>
                    <span className="text-red-900 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center bg-green-50 rounded-lg p-6">
                <p className="text-lg text-green-800 font-medium">
                  Every second is recorded. Every transaction is traceable. Every decision is visible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              How the Parking Lot Management App Works
            </h2>
            
            <div className="space-y-8">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">1</div>
                  <h4 className="text-xl font-bold text-blue-900">Setup Your Hive</h4>
                </div>
                <p className="text-blue-800 text-lg leading-relaxed">
                  Create your parking space — called a Hive — directly through the app. Add details like location, 
                  area, slot capacity, and pricing structure. Invite team members securely and manage permissions 
                  with PIN-protected access.
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">2</div>
                  <h4 className="text-xl font-bold text-green-900">Vehicle Operations</h4>
                </div>
                <p className="text-green-800 text-lg leading-relaxed">
                  When vehicles arrive, staff scan them using the mobile camera. The app detects vehicle type 
                  automatically and begins timing. When vehicles exit, another scan instantly calculates the 
                  charges based on configured rules. Payments are completed digitally.
                </p>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">3</div>
                  <h4 className="text-xl font-bold text-purple-900">Real-Time Monitoring</h4>
                </div>
                <p className="text-purple-800 text-lg leading-relaxed">
                  Meanwhile, the owner sees all activity live — check-ins, check-outs, current occupancy, 
                  total collections, pending payments, and real-time performance. No friction. No confusion. No waiting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Capabilities Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Key Capabilities Parking Owners Rely On
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              The Park Hive Parking Lot Management App delivers deep operational power with simple execution:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {capabilities.map((capability, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                  <div className="flex items-start mb-3">
                    <div className="mr-4 mt-1">
                      {capability.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">{capability.title}</h4>
                      <p className="text-gray-600">{capability.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <div className="flex items-start mb-3">
                  <div className="mr-4 mt-1">
                    <Eye className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Track every check-in, check-out, and transaction in real time</h4>
                    <p className="text-gray-600">Complete operational visibility</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <div className="flex items-start mb-3">
                  <div className="mr-4 mt-1">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Export reports for auditing, settlement, and documentation</h4>
                    <p className="text-gray-600">Comprehensive reporting capabilities</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <div className="flex items-start mb-3">
                  <div className="mr-4 mt-1">
                    <Smartphone className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Operate multiple locations without physically being present</h4>
                    <p className="text-gray-600">Remote management capabilities</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <div className="flex items-start mb-3">
                  <div className="mr-4 mt-1">
                    <WifiOff className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Continue smooth operations even during internet instability</h4>
                    <p className="text-gray-600">Offline functionality built-in</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <p className="text-lg font-medium text-gray-700">
                Everything operates with the clarity of <span className="text-blue-600 font-bold">digital control.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Impact Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              The Real Impact for Parking Lot Owners
            </h2>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed text-center mb-6">
                When the app replaces manual process and uncertainty, operations become measurable and predictable.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4 text-center">
                  <h4 className="font-bold text-gray-900">Queues move faster</h4>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <h4 className="font-bold text-gray-900">Customers leave satisfied</h4>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <h4 className="font-bold text-gray-900">Staff work with accountability</h4>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <h4 className="font-bold text-gray-900">Revenue stays transparent</h4>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <h4 className="font-bold text-gray-900">Ownership becomes stress-free</h4>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <h4 className="font-bold text-gray-900">Operations become intelligent</h4>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-red-900 mb-3">Before: Manual Operations</h4>
                <p className="text-red-800 italic text-lg">"What happened today?"</p>
                <p className="text-red-700 mt-2">Owners stop firefighting and wondering.</p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-green-900 mb-3">After: Digital Control</h4>
                <p className="text-green-800 italic text-lg">"This is exactly where we stand."</p>
                <p className="text-green-700 mt-2">They start managing intelligently.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6">
              Perfect for Every Type of Parking Operation
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Whether you manage any type of parking facility — Park Hive adapts to the real world. 
              From 20 slots to 20,000 — the experience stays smooth.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {operations.map((operation, index) => (
                <div key={index} className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{operation}</span>
                </div>
              ))}
            </div>
            
            <div className="text-center bg-blue-600 text-white rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-2">Scale Without Limits</h3>
              <p className="text-xl">From 20 slots to 20,000 — the experience stays smooth.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Leaders Choose Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Why Leaders Choose Park Hive
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-blue-900 mb-3">🔍 Visibility</h4>
                <p className="text-blue-800">
                  Gives owners the most valuable power in business: complete operational visibility.
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-green-900 mb-3">🛡️ Security</h4>
                <p className="text-green-800">
                  Removes the fear of manual errors and revenue leakage completely.
                </p>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-purple-900 mb-3">⚡ Empowerment</h4>
                <p className="text-purple-800">
                  Empowers staff without surrendering control to owners.
                </p>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-orange-900 mb-3">📊 Confidence</h4>
                <p className="text-orange-800">
                  Every second the parking lot operates, it operates with confidence.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-gray-900 to-blue-900 text-white rounded-xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-4">Modern Parking Business Truth</h3>
              <p className="text-xl mb-4">A modern parking business doesn't run on paper.</p>
              <p className="text-xl">
                It runs on intelligence — and <span className="text-blue-300 font-bold">Park Hive delivers exactly that.</span>
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Book a Live Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200"
              >
                Request a Demo
              </button>
            </div>
            
            <p className="text-gray-600 mt-6">
              See how the app transforms your parking operations in minutes.<br />
              Watch workflows live. Test the entry and exit system. Understand reporting.<br />
              Feel the difference between manual guesswork and digital command.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h4>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Closing Thought */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Closing Thought</h2>
            
            <div className="space-y-6 text-lg leading-relaxed">
              <p>Every parking space has two stories:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-900/30 rounded-lg p-6">
                  <h4 className="font-bold text-red-300 mb-2">Story 1</h4>
                  <p>One filled with confusion, slow queues, staff conflict, and hidden losses.</p>
                </div>
                <div className="bg-green-900/30 rounded-lg p-6">
                  <h4 className="font-bold text-green-300 mb-2">Story 2</h4>
                  <p>Another powered by smooth flow, real-time clarity, and financial confidence.</p>
                </div>
              </div>
              
              <p className="text-xl">The difference isn't luck.</p>
              <p className="text-2xl font-bold text-blue-300">It's technology.</p>
              <p className="text-2xl font-bold text-yellow-300">Park Hive is that difference.</p>
            </div>
            
            <div className="mt-12">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-colors duration-200 inline-flex items-center"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
                </svg>
                Start running your parking lot with clarity, speed, and control
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParkingLotManagementApp;